import { Box, Flex, Stack, Text } from "flicket-ui";
import { Select, Badge } from "~components";
import { getQuantityOptions } from "~lib/helpers/getQuantityOptions";
import { FixedRatioImage } from "~components/common/Image";
import { IReleaseTicket } from "~features/nonseated-reservation/nonseated-reservation.types";
import Price from "~components/common/Price";
import PriceWithFee from "~components/reservation/common/common.PriceWithFee";

interface INonSeatedListItem {
  ticket: IReleaseTicket;
  onChange: (value: number) => void;
  quantity: number;
  showHiddenFees: boolean;
  showHighDemandBadge: boolean;
}

export const NonSeatedListItem = ({
  ticket,
  onChange,
  quantity,
  showHiddenFees,
  showHighDemandBadge,
}: INonSeatedListItem) => {
  // Will be used to check if the ticket has the enhanced view
  const showEnhanced = true;

  return (
    <>
      {!showEnhanced && (
        <BasicTicketItem
          ticket={ticket}
          onChange={onChange}
          quantity={quantity}
          showHiddenFees={showHiddenFees}
          showHighDemandBadge={showHighDemandBadge}
        />
      )}

      {showEnhanced && (
        <EnhancedTicketItem
          ticket={ticket}
          onChange={onChange}
          quantity={quantity}
          showHiddenFees={showHiddenFees}
          showHighDemandBadge={showHighDemandBadge}
        />
      )}
    </>
  );
};

function EnhancedTicketItem({
  onChange,
  ticket,
  showHiddenFees = false,
  quantity = undefined,
  showHighDemandBadge,
}: INonSeatedListItem) {
  return (
    <Flex
      alignItems="center"
      mb={3}
      id={ticket.id}
      p={2}
      boxShadow="card"
      borderRadius="md"
    >
      <Stack
        gap={2}
        direction={["vertical", "vertical", "horizontal"]}
        justifyContent="space-between"
        flex={1}
      >
        {ticket.image && (
          <Box>
            <FixedRatioImage
              image={ticket.image}
              alt={ticket.name}
              ratio={"196 / 130"}
              width={{ _: "100%", xs: 196, sm: 260 }}
              borderRadius="sm"
            />
          </Box>
        )}
        <Box flex={1}>
          <Stack gap={3} direction="horizontal" justifyContent="space-between">
            <Box flex={1}>
              <Text variant="header.S">{ticket.name}</Text>
              <TicketPriceAndFee ticket={ticket} showFees={showHiddenFees} />
            </Box>
            <Box>
              <QuantitySelector
                ticket={ticket}
                onChange={onChange}
                quantity={quantity}
              />
            </Box>
          </Stack>
          {showHighDemandBadge && <Badge>In High demand</Badge>}
          <Text variant="regular">{/* TODO: summary text */}</Text>
        </Box>
      </Stack>
    </Flex>
  );
}

function BasicTicketItem({
  onChange,
  ticket,
  showHiddenFees = false,
  quantity = undefined,
  showHighDemandBadge,
}: INonSeatedListItem) {
  return (
    <Box
      alignItems="center"
      mb={3}
      id={ticket.id}
      p={2}
      boxShadow="card"
      borderRadius="md"
    >
      <Stack gap={3} direction="horizontal" justifyContent="space-between">
        <Box flex={1}>
          <Text variant="header.S">{ticket.name}</Text>
          <TicketPriceAndFee ticket={ticket} showFees={showHiddenFees} />
          {showHighDemandBadge && <Badge>In High demand</Badge>}
        </Box>
        <Box>
          <QuantitySelector
            ticket={ticket}
            onChange={onChange}
            quantity={quantity}
          />
        </Box>
      </Stack>
    </Box>
  );
}

function TicketPriceAndFee({
  ticket,
  showFees,
}: {
  showFees: boolean;
  ticket: IReleaseTicket;
}) {
  return (
    <Box>
      <Text as="span" variant="regular">
        <Price price={ticket.price} />
      </Text>
      {showFees && (
        <Text as="span" variant="small">
          {" "}
          <PriceWithFee.Fee showFees={true} ticket={ticket} />
        </Text>
      )}
    </Box>
  );
}

// Will be replaced with a numeric stepper as part of this feature
function QuantitySelector({
  ticket,
  onChange,
  quantity,
}: Pick<INonSeatedListItem, "onChange" | "quantity" | "ticket">) {
  return (
    <Select
      minW="98px"
      options={getQuantityOptions(
        ticket.minPurchaseQuantity,
        ticket.maxPurchaseQuantity
      )}
      value={quantity}
      onChange={onChange}
      small
      useSingleValue
    />
  );
}
